import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IdentityProvider, useStytch, useStytchUser } from '@stytch/react';
import { parseOAuthAuthorizeParams, StytchEventType } from '@stytch/vanilla-js';
import { Navigate, useLocation } from 'react-router';
import SigningInScreen from './SigningInScreen';

function AuthorizeSession({ search, userId }) {
  const stytch = useStytch();
  const params = useMemo(() => parseOAuthAuthorizeParams(new URLSearchParams(search)), [search]);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading' });
  const requestRef = useRef(null);

  useEffect(() => {
    if (params.error) return;

    let active = true;
    let request = requestRef.current;
    // StrictMode replays effects. Keep the same in-flight promise so it cannot
    // issue two authorization codes; only the active effect commits the result.
    if (!request || request.params !== params || request.attempt !== attempt || request.stytch !== stytch || request.userId !== userId) {
      request = { params, attempt, stytch, userId };
      request.promise = (async () => {
        const preflight = await stytch.idp.oauthAuthorizeStart(params.result);
        if (preflight.consent_required) return { status: 'consent' };

        // The provider decides whether consent is required. Match its SDK's
        // no-consent path without ever displaying the generic success card.
        const response = await stytch.idp.oauthAuthorizeSubmit({ ...params.result, consent_granted: false });
        if (!response.redirect_uri) throw new Error('Missing authorization redirect');
        return { status: 'redirecting', redirectUrl: response.redirect_uri };
      })();
      requestRef.current = request;
    }

    request.promise.then(result => {
      if (!active) return;
      setState(result);
      if (result.redirectUrl) window.location.replace(result.redirectUrl);
    }).catch(() => {
      if (active) setState({ status: 'error' });
    });

    return () => { active = false; };
  }, [attempt, params, stytch, userId]);

  const consentCallbacks = useMemo(() => ({
    onEvent: event => {
      if (event.type === StytchEventType.OAuthAuthorizeFlowComplete) {
        setState({ status: 'redirecting' });
      }
    },
    onError: () => setState({ status: 'error' }),
  }), []);

  if (params.error) {
    return <SigningInScreen title="Unable to sign in" message="This sign-in link is incomplete. Return to the app and start sign-in again."><div role="alert">Invalid authorization request.</div></SigningInScreen>;
  }

  if (state.status === 'error') {
    return (
      <SigningInScreen title="Unable to finish signing in" message="Please try again.">
        <button className="login-button" onClick={() => {
          setState({ status: 'loading' });
          setAttempt(value => value + 1);
        }}>Try again</button>
      </SigningInScreen>
    );
  }

  if (state.status === 'consent') {
    // MCP and third-party clients retain Stytch's complete consent UI,
    // including scopes and denial. Never auto-grant a required consent.
    return <IdentityProvider callbacks={consentCallbacks} />;
  }

  return <SigningInScreen />;
}

export default function OAuthAuthorizePage() {
  const { user, isInitialized } = useStytchUser();
  const location = useLocation();

  if (!isInitialized) return <SigningInScreen />;
  if (!user) {
    const returnUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect_url=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return <AuthorizeSession key={`${user.user_id}:${location.search}`} search={location.search} userId={user.user_id} />;
}
